import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

function parseJWT(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch { return null; }
}

function isTokenExpired(token) {
  const payload = parseJWT(token);
  if (!payload?.exp) return true;
  return payload.exp * 1000 < Date.now();
}

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token)                { setLoading(false); return; }
    if (isTokenExpired(token)) { logout(); setLoading(false); return; }
    try {
      const res = await authAPI.getMe();
      setUser(res.data.user);
    } catch { logout(); }
    finally  { setLoading(false); }
  }, [logout]);

  useEffect(() => {
    loadUser();
    const interval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token && isTokenExpired(token)) { logout(); window.location.href = '/login'; }
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [loadUser, logout]);

  const login = async (googleToken) => {
    const res = await authAPI.googleLogin(googleToken);
    const { token, user: u } = res.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(u));
    setUser(u);
    return u;
  };

  const isAdmin   = user?.role === 'admin_editor' || user?.role === 'admin_viewer';
  const isEditor  = user?.role === 'admin_editor';
  const isViewer  = user?.role === 'admin_viewer';
  const isAboutu  = user?.role === 'aboutu';
  // Any role that can see the dashboard
  const canViewDashboard = isAdmin || isAboutu;

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout,
      isAdmin, isEditor, isViewer, isAboutu, canViewDashboard
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
